<template>
    <div>
        <div v-if="servers.length > 0" class="server-dash my-2">
            <v-sheet v-for="server in servers" :key="server.name" outlined rounded="lg" :class="['server-tile', `server-tile--${serverStatus(server)}`]">
                <div class="server-tile__head">
                    <span :class="['server-dot', `server-dot--${serverStatus(server)}`]" />
                    <span class="server-tile__name" :title="server.name">{{ shortName(server.name) }}</span>
                    <v-spacer />
                    <span v-if="server.available" class="server-tile__uptime" title="Tempo ligado">
                        <v-icon x-small class="mr-1">schedule</v-icon>{{ formatDuration(server.uptime.seconds) }}
                    </span>
                    <span v-else class="server-tile__uptime error--text">offline</span>
                </div>

                <template v-if="server.available">
                    <div v-for="meter in meters(server)" :key="meter.label" class="server-meter" :title="`${meter.label}: ${formatBytes(meter.used)} de ${formatBytes(meter.total)}`">
                        <span class="server-meter__label">{{ meter.label }}</span>
                        <div class="server-meter__track">
                            <div class="server-meter__fill" :class="`server-meter__fill--${level(meter.percent)}`" :style="{ width: `${Math.min(meter.percent, 100)}%` }" />
                        </div>
                        <span class="server-meter__value" :class="{ 'error--text': level(meter.percent) === 'critical', 'warning--text': level(meter.percent) === 'warning' }">{{ meter.percent }}%</span>
                    </div>
                </template>

                <div v-if="server.alarms.length > 0" class="server-tile__alarms">
                    <div v-for="(alarm, index) in server.alarms" :key="index" :class="alarm.critical ? 'error--text' : 'warning--text'">
                        <v-icon x-small :color="alarm.critical ? 'error' : 'warning'" class="mr-1">{{ alarm.critical ? "error" : "warning" }}</v-icon>{{ alarm.text }}
                    </div>
                </div>
            </v-sheet>
            <div class="server-dash__timer" :style="{ width: `${100 - timerProgress}%` }" />
        </div>

        <ExpandPanelSimple :title="title" barClass="critical white--text" v-if="items.length > 0">
            <v-card color="error white--text" class="elevation-24 my-2" >
                <v-card-text>
                    <v-flex xs12 class="mb-1">
                        <v-progress-linear progress color="white" :value="timerProgress" />
                    </v-flex>
                    <v-data-table :headers="headers" :items="items" class="elevation-2" hide-default-footer :items-per-page.sync="page">
                        <template v-slot:[`item.alarms`]="{ item }">
                            <div v-if="item.alarms.length > 0" class="py-1">
                                <v-chip v-for="(alarm, index) in item.alarms" :key="index" small label dark :color="alarm.critical ? 'error' : 'warning'" class="ma-1">
                                    <v-icon small left>{{ alarm.critical ? "error" : "warning" }}</v-icon>
                                    {{ alarm.text }}
                                </v-chip>
                            </div>
                            <span v-else class="grey--text">Alarme sem detalhes</span>
                        </template>
                        <template v-slot:[`item.id`]="{ item }">
                            <v-btn v-if="item.id" class="success" x-small dark :href="href(`/board/${item.name}`)" target="_blank"> Detalhes </v-btn>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </ExpandPanelSimple>
    </div>
</template>

<script>
import ExpandPanelSimple from "@/components/core/ExpandPanelSimple.vue";
import { boardAlarms } from "@/helpers/boardAlarms";
import { SERVERS, serverAlarms, formatBytes, formatDuration } from "@/helpers/serverAlarms";

export default {

    components: { ExpandPanelSimple },

    data() {
        return {
            page: -1,
            items: [],
            servers: [],
            timer: null,
            timerProgress: 10, // 3 seconds, after 30 seconds
            headers: [
                { text: "Identificador", value: "name", align: "right", sortable: false },
                { text: "Alarmes", value: "alarms", align: "left", sortable: false },
                { text: "-", value: "id", align: "center", sortable: false },
            ],
        };
    },
    
    computed: {
        title() {
            return `${this.items.length} Gateway(s) com alarme.`;
        }
    },

    created() {
        // this.update();
        this.timer = setInterval(this.timerTick, 300);
    },

    methods: {
        formatBytes,
        formatDuration,
        shortName(name) {
            return name.replace(/^backend\./, "");
        },
        level(percent) {
            if (percent > 90) return "critical";
            if (percent > 80) return "warning";
            return "ok";
        },
        serverStatus(server) {
            if (server.alarms.some((alarm) => alarm.critical)) return "critical";
            if (server.alarms.length > 0) return "warning";
            return "ok";
        },
        meters(server) {
            const meters = [{ label: "RAM", used: server.memory.used, total: server.memory.total, percent: server.memory.percent }];
            (server.disks || []).forEach((disk) => meters.push({ label: disk.mount, used: disk.used, total: disk.total, percent: disk.percent }));
            return meters;
        },
        href(url) {
            return `${window.location.origin}${url}`;
        },
        timerTick() {
            if (this.timerProgress > 0) {
                this.timerProgress--;
            } else {
                this.update();
                this.timerProgress = 100;
            }
        },
        async update() {
            await this.$http
                .get(`api/v3/board/error`, { showLoading: false })
                .then((result) => {
                    this.items = result.map((board) => ({ ...board, alarms: boardAlarms(board) }));
                }).catch(() => {
                    this.$eventHub.$emit("msgError", "Erro ao buscar gateways.");
                });
            // Falhas de cada servidor viram alarme no próprio card (ver serverAlarms)
            const servers = await Promise.all(SERVERS.map((server) => this.$http
                .get(`${server.api}api/v3/server/health`, { showLoading: false, skipErrorHandler: true })
                .catch((error) => ({ available: false, unreachable: !error.response, status: error.response ? error.response.status : null }))
                .then((result) => ({ ...result, name: server.name }))));
            this.servers = servers.map((server) => ({ ...server, alarms: serverAlarms(server) }));
        },
    },

    beforeDestroy() {
        clearInterval(this.timer);
    },
}
</script>
<style scoped>
.v-card {
    display: flex !important;
    flex-direction: column;
}

.v-card__text {
    flex-grow: 1;
    overflow: auto;
}

.server-dash {
    position: relative;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 8px;
    padding-bottom: 4px;
}

.server-dash__timer {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    border-radius: 2px;
    background: var(--v-primary-base);
    opacity: 0.35;
    transition: width 0.3s linear;
}

.server-tile {
    padding: 8px 12px;
    border-left-width: 4px !important;
    font-size: 12px;
    line-height: 1.4;
}

.server-tile--ok { border-left-color: var(--v-success-base) !important; }
.server-tile--warning { border-left-color: var(--v-warning-base) !important; }
.server-tile--critical { border-left-color: var(--v-error-base) !important; }

.server-tile__head {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
}

.server-tile__name {
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.server-tile__uptime {
    font-size: 11px;
    opacity: 0.7;
    white-space: nowrap;
    margin-left: 8px;
}

.server-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    flex-shrink: 0;
}

.server-dot--ok { background: var(--v-success-base); box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2); }
.server-dot--warning { background: var(--v-warning-base); box-shadow: 0 0 0 3px rgba(251, 140, 0, 0.2); }
.server-dot--critical { background: var(--v-error-base); animation: server-pulse 1.4s ease-in-out infinite; }

@keyframes server-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.6); }
    50% { box-shadow: 0 0 0 5px rgba(255, 82, 82, 0); }
}

.server-meter {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 3px;
}

.server-meter__label {
    width: 52px;
    flex-shrink: 0;
    opacity: 0.7;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.server-meter__track {
    flex-grow: 1;
    height: 6px;
    border-radius: 3px;
    background: rgba(128, 128, 128, 0.2);
    overflow: hidden;
}

.server-meter__fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.6s ease;
}

.server-meter__fill--ok { background: var(--v-success-base); }
.server-meter__fill--warning { background: var(--v-warning-base); }
.server-meter__fill--critical { background: var(--v-error-base); }

.server-meter__value {
    width: 32px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
}

.server-tile__alarms {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px dashed rgba(128, 128, 128, 0.3);
    font-size: 11px;
}
</style>
